import React, { useMemo, useState, useEffect } from 'react';
import { collection, orderBy, query, limit, getCountFromServer } from 'firebase/firestore';
import { useCollection } from 'react-firebase-hooks/firestore';
import db from '../firebase';
import { planecheckToggleLane } from '../fb-functions';

const LANE_NAMES = ['brightdata', 'brightdata-eu', 'zyte', 'zenrows-http', 'scrapingbee-api', 'zenrows-api', 'zenrows-proxy', 'oxylabs-api'];

const STATE_COLORS = {
  HEALTHY: 'bg-green-100 text-green-800',
  PROBATION: 'bg-yellow-100 text-yellow-800',
  PAUSED: 'bg-red-100 text-red-800',
};

function PipelineMonitor() {
  // 1. Latest snapshot
  const snapshotsQuery = query(
    collection(db, 'planecheck_snapshots'),
    orderBy('createdAt', 'desc'),
    limit(5),
  );
  const [snapshots, snapshotsLoading, snapshotsError] = useCollection(snapshotsQuery);

  const [selectedSnapIndex, setSelectedSnapIndex] = useState(0);
  const selectedSnap = snapshots?.docs?.[selectedSnapIndex];
  const snapData = selectedSnap?.data();
  const snapshotId = selectedSnap?.id;

  // Unique listing count from subcollection
  const [uniqueListingCount, setUniqueListingCount] = useState(null);
  useEffect(() => {
    if (!snapshotId) return;
    getCountFromServer(collection(db, 'planecheck_snapshots', snapshotId, 'listings'))
      .then((snap) => setUniqueListingCount(snap.data().count))
      .catch(() => setUniqueListingCount(null));
  }, [snapshotId]);

  // 2. Lane states (3 docs)
  const [laneStates, lanesLoading] = useCollection(
    collection(db, 'planecheck_lane_state'),
  );

  // 3. Recent attempts (last 200 by timestamp, filter by snapshotId client-side)
  const attemptsQuery = query(
    collection(db, 'planecheck_detail_attempts'),
    orderBy('timestamp', 'desc'),
    limit(200),
  );
  const [attempts, attemptsLoading] = useCollection(attemptsQuery);

  // Filter attempts to current snapshot
  const snapshotAttempts = useMemo(() => {
    if (!attempts || !snapshotId) return [];
    return attempts.docs
      .filter((d) => d.data().snapshotId === snapshotId)
      .map((d) => ({ id: d.id, ...d.data() }));
  }, [attempts, snapshotId]);

  // Compute lane-level pacing stats from attempts
  const laneStats = useMemo(() => {
    const stats = {};
    LANE_NAMES.forEach((lane) => {
      const laneAttempts = snapshotAttempts
        .filter((a) => a.lane === lane && a.timestamp)
        .sort((a, b) => {
          const aMs = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
          const bMs = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
          return aMs - bMs; // oldest first for interval calc
        });

      const intervals = [];
      for (let i = 1; i < laneAttempts.length; i++) {
        const prevMs = laneAttempts[i - 1].timestamp?.toMillis
          ? laneAttempts[i - 1].timestamp.toMillis() : 0;
        const currMs = laneAttempts[i].timestamp?.toMillis
          ? laneAttempts[i].timestamp.toMillis() : 0;
        if (prevMs && currMs) {
          intervals.push((currMs - prevMs) / 1000);
        }
      }

      const successCount = laneAttempts.filter((a) => a.status === 'success').length;
      const soft404Count = laneAttempts.filter((a) => a.status === 'soft404').length;
      const errorCount = laneAttempts.filter(
        (a) => !['success', 'soft404', 'removed'].includes(a.status),
      ).length;

      stats[lane] = {
        total: laneAttempts.length,
        successCount,
        soft404Count,
        errorCount,
        successRate: laneAttempts.length > 0
          ? ((successCount / laneAttempts.length) * 100).toFixed(1) : '0.0',
        avgIntervalSec: intervals.length > 0
          ? (intervals.reduce((a, b) => a + b, 0) / intervals.length).toFixed(1) : '-',
        minIntervalSec: intervals.length > 0
          ? Math.min(...intervals).toFixed(1) : '-',
        maxIntervalSec: intervals.length > 0
          ? Math.max(...intervals).toFixed(1) : '-',
      };
    });
    return stats;
  }, [snapshotAttempts]);

  // Windowed stats (30min and 1hr) from all attempts (not snapshot-filtered)
  const windowedStats = useMemo(() => {
    if (!attempts) return null;
    const now = Date.now();
    const cutoff30 = now - 30 * 60 * 1000;
    const cutoff60 = now - 60 * 60 * 1000;

    const w30 = {};
    const w60 = {};
    let total30 = { success: 0, attempts: 0 };
    let total60 = { success: 0, attempts: 0 };

    attempts.docs.forEach((doc) => {
      const d = doc.data();
      const ts = d.timestamp?.toMillis ? d.timestamp.toMillis() : 0;
      if (!ts) return;
      const lane = d.lane || 'unknown';
      const isSuccess = d.status === 'success';

      if (ts >= cutoff60) {
        if (!w60[lane]) w60[lane] = { success: 0, soft404: 0, error: 0, total: 0 };
        w60[lane].total++;
        total60.attempts++;
        if (isSuccess) { w60[lane].success++; total60.success++; }
        else if (d.status === 'soft404') w60[lane].soft404++;
        else w60[lane].error++;
      }
      if (ts >= cutoff30) {
        if (!w30[lane]) w30[lane] = { success: 0, soft404: 0, error: 0, total: 0 };
        w30[lane].total++;
        total30.attempts++;
        if (isSuccess) { w30[lane].success++; total30.success++; }
        else if (d.status === 'soft404') w30[lane].soft404++;
        else w30[lane].error++;
      }
    });

    return { w30, w60, total30, total60 };
  }, [attempts]);

  // Overall throughput from snapshot doc
  const throughputStats = useMemo(() => {
    if (!snapData) return null;
    const total = snapData.totalListings || 0;
    const rawCompleted = (snapData.completedListings || 0) + (snapData.removedListings || 0);
    const failed = snapData.failedListings || 0;
    const completed = Math.min(rawCompleted, total - failed);
    const createdAt = snapData.createdAt;

    if (!createdAt || completed === 0) {
      return { completed, total, failed, rate: 0, etaHours: '-', percentage: 0 };
    }

    const startMs = createdAt.toMillis ? createdAt.toMillis() : Date.now();
    const elapsedHours = (Date.now() - startMs) / 3600000;
    const rate = elapsedHours > 0 ? completed / elapsedHours : 0;
    const remaining = total - completed - failed;
    const etaHours = rate > 0 ? (remaining / rate).toFixed(1) : '-';
    const percentage = total > 0 ? ((completed / total) * 100).toFixed(1) : '0.0';

    return { completed, total, failed, remaining, rate: rate.toFixed(1), etaHours, percentage, elapsedHours: elapsedHours.toFixed(1) };
  }, [snapData]);

  // Lane state data from Firestore
  const laneStateMap = useMemo(() => {
    const map = {};
    if (!laneStates) return map;
    laneStates.docs.forEach((d) => {
      map[d.id] = d.data();
    });
    return map;
  }, [laneStates]);

  // Global rate limit
  const [globalState] = useCollection(collection(db, 'planecheck_global_state'));
  const globalData = globalState?.docs?.[0]?.data();

  // Format timestamp for display
  const formatTs = (ts) => {
    if (!ts || !ts.toDate) return '-';
    const d = ts.toDate();
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatTsRelative = (ts) => {
    if (!ts || !ts.toDate) return '-';
    const diffSec = (Date.now() - ts.toDate().getTime()) / 1000;
    if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    return `${(diffSec / 3600).toFixed(1)}h ago`;
  };

  const formatDuration = (hours) => {
    if (hours < 1) return Math.round(hours * 60) + 'min';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  const formatEtaTime = (date) => {
    return date.toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const [togglingLane, setTogglingLane] = useState(null);

  const handleToggleLane = async (lane, action) => {
    setTogglingLane(lane);
    try {
      const res = await planecheckToggleLane({ lane, action });
      if (!res.success) {
        console.error('Failed to toggle lane:', res.error);
      }
    } catch (err) {
      console.error('Failed to toggle lane:', err);
    } finally {
      setTogglingLane(null);
    }
  };

  if (snapshotsLoading) return <p className="text-sm text-gray-500">Loading pipeline data...</p>;
  if (snapshotsError) return <p className="text-sm text-red-500">Error loading pipeline data</p>;
  if (!snapData) return <p className="text-sm text-gray-400">No pipeline snapshots found</p>;

  return (
    <div className="my-4 space-y-4">
      {/* Snapshot Progress */}
      <div className="rounded border border-blue-gray-200 p-4 bg-white shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-blue-gray-800">Pipeline V2 Monitor</h3>
            <select
              value={selectedSnapIndex}
              onChange={(e) => setSelectedSnapIndex(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-blue-500"
            >
              {snapshots?.docs?.map((doc, i) => {
                const d = doc.data();
                const label = d.createdAt?.toDate
                  ? d.createdAt.toDate().toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : doc.id.slice(0, 8);
                return (
                  <option key={doc.id} value={i}>
                    {label} — {d.status || 'unknown'}
                  </option>
                );
              })}
            </select>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            snapData.status === 'completed' ? 'bg-green-100 text-green-700' :
            snapData.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            'bg-red-100 text-red-700'
          }`}>
            {snapData.status}
          </span>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Snapshot: {snapshotId?.slice(0, 8)}... | Scrape: {snapData.scrapeId?.slice(0, 12)}...
          {snapData.createdAt && ` | Started: ${formatTsRelative(snapData.createdAt)}`}
        </p>

        {/* Progress bar */}
        {throughputStats && (
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span>{throughputStats.completed} / {throughputStats.total} completed</span>
              <span>{throughputStats.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(parseFloat(throughputStats.percentage || 0), 100)}%` }}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 text-xs">
              <div>Rate: <strong>{throughputStats.rate}/hr</strong></div>
              <div>ETA: <strong>{throughputStats.etaHours}h</strong></div>
              <div>Failed: <strong>{throughputStats.failed}</strong></div>
              <div>Remaining: <strong>{throughputStats.remaining}</strong></div>
              <div>Elapsed: <strong>{throughputStats.elapsedHours}h</strong></div>
              <div>Unique IDs: <strong>{uniqueListingCount !== null ? uniqueListingCount : '...'}</strong>
                {uniqueListingCount !== null && throughputStats.total !== uniqueListingCount && (
                  <span className="text-yellow-600 ml-1">({throughputStats.total - uniqueListingCount} dupes)</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pages progress */}
        <div className="text-xs text-gray-600">
          Pages crawled: {snapData.pagesCompleted || 0} / {snapData.totalPages || '?'}
        </div>
      </div>

      {/* Lane Performance Table + ETA */}
      {windowedStats && (
        <div className="rounded border border-blue-gray-200 p-4 bg-white shadow-sm">
          <h3 className="font-bold text-blue-gray-800 mb-3">Lane Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1.5 pr-3 font-medium">Lane</th>
                  <th className="py-1.5 pr-3 font-medium text-center">30min Rate</th>
                  <th className="py-1.5 pr-3 font-medium text-center">30min %</th>
                  <th className="py-1.5 pr-3 font-medium text-center">1hr Rate</th>
                  <th className="py-1.5 pr-3 font-medium text-center">1hr %</th>
                  <th className="py-1.5 pr-3 font-medium text-center">30min Detail</th>
                  <th className="py-1.5 font-medium text-center">1hr Detail</th>
                </tr>
              </thead>
              <tbody>
                {LANE_NAMES.map((lane) => {
                  const s30 = windowedStats.w30[lane] || { success: 0, total: 0, soft404: 0, error: 0 };
                  const s60 = windowedStats.w60[lane] || { success: 0, total: 0, soft404: 0, error: 0 };
                  const pct30 = s30.total > 0 ? ((s30.success / s30.total) * 100).toFixed(0) : '-';
                  const pct60 = s60.total > 0 ? ((s60.success / s60.total) * 100).toFixed(0) : '-';
                  const pct30Color = pct30 === '-' ? '' : parseFloat(pct30) >= 90 ? 'text-green-700' : parseFloat(pct30) >= 70 ? 'text-yellow-700' : 'text-red-700';
                  const pct60Color = pct60 === '-' ? '' : parseFloat(pct60) >= 90 ? 'text-green-700' : parseFloat(pct60) >= 70 ? 'text-yellow-700' : 'text-red-700';
                  return (
                    <tr key={lane} className="border-b border-gray-100">
                      <td className="py-1.5 pr-3 font-semibold">{lane}</td>
                      <td className="py-1.5 pr-3 text-center font-medium">~{s30.success * 2}/hr</td>
                      <td className={`py-1.5 pr-3 text-center font-bold ${pct30Color}`}>{pct30 === '-' ? '-' : pct30 + '%'}</td>
                      <td className="py-1.5 pr-3 text-center font-medium">~{s60.success}/hr</td>
                      <td className={`py-1.5 pr-3 text-center font-bold ${pct60Color}`}>{pct60 === '-' ? '-' : pct60 + '%'}</td>
                      <td className="py-1.5 pr-3 text-center text-gray-500">{s30.success}ok {s30.soft404}s4 {s30.error}err</td>
                      <td className="py-1.5 text-center text-gray-500">{s60.success}ok {s60.soft404}s4 {s60.error}err</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-gray-300 font-bold">
                  <td className="py-1.5 pr-3">TOTAL</td>
                  <td className="py-1.5 pr-3 text-center">~{windowedStats.total30.success * 2}/hr</td>
                  <td className={`py-1.5 pr-3 text-center ${
                    windowedStats.total30.attempts > 0
                      ? ((windowedStats.total30.success / windowedStats.total30.attempts) * 100) >= 90 ? 'text-green-700' : 'text-yellow-700'
                      : ''
                  }`}>
                    {windowedStats.total30.attempts > 0
                      ? ((windowedStats.total30.success / windowedStats.total30.attempts) * 100).toFixed(1) + '%'
                      : '-'}
                  </td>
                  <td className="py-1.5 pr-3 text-center">~{windowedStats.total60.success}/hr</td>
                  <td className={`py-1.5 pr-3 text-center ${
                    windowedStats.total60.attempts > 0
                      ? ((windowedStats.total60.success / windowedStats.total60.attempts) * 100) >= 90 ? 'text-green-700' : 'text-yellow-700'
                      : ''
                  }`}>
                    {windowedStats.total60.attempts > 0
                      ? ((windowedStats.total60.success / windowedStats.total60.attempts) * 100).toFixed(1) + '%'
                      : '-'}
                  </td>
                  <td className="py-1.5 pr-3 text-center text-gray-500">
                    {windowedStats.total30.success}/{windowedStats.total30.attempts}
                  </td>
                  <td className="py-1.5 text-center text-gray-500">
                    {windowedStats.total60.success}/{windowedStats.total60.attempts}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Windowed ETA */}
          {throughputStats && throughputStats.remaining > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-4 text-xs">
              {(() => {
                const rate30 = windowedStats.total30.success * 2;
                const rate60 = windowedStats.total60.success;
                const eta30 = rate30 > 0 ? throughputStats.remaining / rate30 : null;
                const eta60 = rate60 > 0 ? throughputStats.remaining / rate60 : null;
                const etaDate30 = eta30 ? new Date(Date.now() + eta30 * 3600000) : null;
                const etaDate60 = eta60 ? new Date(Date.now() + eta60 * 3600000) : null;
                return (
                  <>
                    <div>
                      <span className="text-gray-500">ETA (30min rate):</span>{' '}
                      {eta30 ? <strong>{formatDuration(eta30)} &rarr; {formatEtaTime(etaDate30)}</strong> : <span className="text-gray-400">-</span>}
                    </div>
                    <div>
                      <span className="text-gray-500">ETA (1hr rate):</span>{' '}
                      {eta60 ? <strong>{formatDuration(eta60)} &rarr; {formatEtaTime(etaDate60)}</strong> : <span className="text-gray-400">-</span>}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Lane States */}
      <div className="rounded border border-blue-gray-200 p-4 bg-white shadow-sm">
        <h3 className="font-bold text-blue-gray-800 mb-3">Lane Health</h3>
        {lanesLoading ? (
          <p className="text-xs text-gray-500">Loading lanes...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {LANE_NAMES.map((lane) => {
              const state = laneStateMap[lane] || {};
              const stats = laneStats[lane] || {};
              return (
                <div key={lane} className="border rounded p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{lane}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        STATE_COLORS[state.state] || 'bg-gray-100 text-gray-600'
                      }`}>
                        {state.state || 'UNKNOWN'}
                      </span>
                      {state.state === 'PAUSED' ? (
                        <button
                          onClick={() => handleToggleLane(lane, 'unpause')}
                          disabled={togglingLane === lane}
                          className="text-[10px] px-2 py-0.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {togglingLane === lane ? '...' : 'Unpause'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleToggleLane(lane, 'pause')}
                          disabled={togglingLane === lane}
                          className="text-[10px] px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          {togglingLane === lane ? '...' : 'Pause'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs space-y-1 text-gray-700">
                    <div className="flex justify-between">
                      <span>Attempts (recent):</span>
                      <strong>{stats.total || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Success rate:</span>
                      <strong>{stats.successRate || '0.0'}%</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Soft-404s:</span>
                      <strong>{stats.soft404Count || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Errors:</span>
                      <strong>{stats.errorCount || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg interval:</span>
                      <strong>{stats.avgIntervalSec || '-'}s</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Min / Max:</span>
                      <strong>{stats.minIntervalSec || '-'}s / {stats.maxIntervalSec || '-'}s</strong>
                    </div>
                    <hr className="border-gray-200" />
                    <div className="flex justify-between">
                      <span>Consec soft-404:</span>
                      <strong>{state.consecutiveSoft404 || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Soft-404 last 10:</span>
                      <strong>{state.soft404CountLast10 || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Reqs since break:</span>
                      <strong>{state.requestsSinceLastBreak || 0}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span>Last request:</span>
                      <strong>{formatTsRelative(state.lastRequestAt)}</strong>
                    </div>
                    {state.state === 'PROBATION' && (
                      <div className="flex justify-between text-yellow-700">
                        <span>Probation remaining:</span>
                        <strong>{state.probationRemaining || 0}</strong>
                      </div>
                    )}
                    {state.state === 'PAUSED' && state.pauseUntil && (
                      <div className="flex justify-between text-red-700">
                        <span>Paused until:</span>
                        <strong>{formatTs(state.pauseUntil)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Global Rate Limit */}
      {globalData && (
        <div className="rounded border border-blue-gray-200 p-4 bg-white shadow-sm">
          <h3 className="font-bold text-blue-gray-800 mb-2">Global Rate Limit</h3>
          <div className="text-xs text-gray-700 flex gap-4">
            <span>Window requests: <strong>{globalData.requestCountWindow || 0} / 45</strong></span>
            <span>Window started: <strong>{formatTsRelative(globalData.windowStartAt)}</strong></span>
          </div>
        </div>
      )}

      {/* Recent Attempts Feed */}
      <div className="rounded border border-blue-gray-200 p-4 bg-white shadow-sm">
        <h3 className="font-bold text-blue-gray-800 mb-2">
          Recent Attempts
          {attemptsLoading && <span className="text-xs text-gray-400 ml-2">loading...</span>}
        </h3>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-gray-500 border-b">
                <th className="py-1 pr-2">Time</th>
                <th className="py-1 pr-2">Listing</th>
                <th className="py-1 pr-2">Lane</th>
                <th className="py-1 pr-2">Status</th>
                <th className="py-1 pr-2">Duration</th>
                <th className="py-1 pr-2">Size</th>
              </tr>
            </thead>
            <tbody>
              {snapshotAttempts.slice(0, 50).map((a) => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-1 pr-2 text-gray-500">{formatTs(a.timestamp)}</td>
                  <td className="py-1 pr-2 font-mono">{a.listingId}</td>
                  <td className="py-1 pr-2">{a.lane}</td>
                  <td className="py-1 pr-2">
                    <span className={`px-1 rounded ${
                      a.status === 'success' ? 'bg-green-100 text-green-700' :
                      a.status === 'soft404' ? 'bg-yellow-100 text-yellow-700' :
                      a.status === 'removed' ? 'bg-purple-100 text-purple-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="py-1 pr-2">{a.durationMs ? `${(a.durationMs / 1000).toFixed(1)}s` : '-'}</td>
                  <td className="py-1 pr-2 text-gray-500">
                    {a.responseSize ? `${Math.round(a.responseSize / 1024)}k` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {snapshotAttempts.length === 0 && !attemptsLoading && (
            <p className="text-xs text-gray-400 py-2">No attempts for this snapshot yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PipelineMonitor;
